import { createSelector } from "@reduxjs/toolkit";
import { multiply, volatileSum, zero } from "~/utils/PriceUtils";
import * as WeightUtils from "~/utils/WeightUtils";
import { type LineItem } from "~/features/cart/cart/models/CartData";
import { type RootState } from "~/app/store";
import {
  selectLineItemsByProduct,
  selectShowOptimePopupOnCheckout,
} from "~/features/cart/cart/redux/selectors/cartSelectors";
import { selectSubtotal } from "~/features/cart/cart/redux/selectors/productEntrySelectors";

export const selectTotalTargetPriceForPackage = (entry: LineItem) =>
  multiply(entry.targetPrice || zero(), entry.quantity);

export const selectTargetSubtotal = (state: RootState, productCode: string) => {
  const packEntries = selectLineItemsByProduct(state)[productCode];
  const totalPerPack = packEntries.map(selectTotalTargetPriceForPackage);
  return volatileSum(...totalPerPack);
};

export const selectActiveQuoteAggregates = createSelector(
  (state: RootState) => state.cartPage,
  (state: RootState) => state.cart,
  selectLineItemsByProduct,
  selectShowOptimePopupOnCheckout,
  (cartPage, cart, lineItemsByProduct, showOptimePopupOnCheckout) => {
    if (cartPage.accountNumber === "") {
      return {
        requestInFlight: false as boolean,
        accountNumber: "",
        totalWeight: WeightUtils.zero(),
        entryIds: [] as number[],
        validCart: true,
        dstError: false,
        showOptimePopupOnCheckout: false,
        productCodes: [] as string[],
      };
    }

    const packEntries = Object.values(cart.packEntriesById);
    const weights = packEntries.map((e) => WeightUtils.multiply(e.weight, e.quantity));

    return {
      requestInFlight: cartPage.requestInFlight,
      accountNumber: cartPage.accountNumber,
      totalWeight: WeightUtils.sum(...weights),
      entryIds: cart.cartEntries,
      validCart: packEntries.reduce((ok, e) => ok && e.valid, true as boolean),
      dstError: cartPage.dstError,
      showOptimePopupOnCheckout,
      productCodes: Object.keys(lineItemsByProduct),
    };
  },
);

export const selectActiveQuoteTotalSum = createSelector(
  (state: RootState) => state,
  selectLineItemsByProduct,
  (state, lineItemsByProduct) => {
    const codes = Object.keys(lineItemsByProduct);
    const perProduct = codes.map((code) => selectSubtotal(state, { productCode: code, isInCheckout: false }));
    return volatileSum(...perProduct);
  },
);

export const selectActiveQuoteTotalTargetSum = createSelector(
  (state: RootState) => state,
  selectLineItemsByProduct,
  (state, lineItemsByProduct) => {
    const codes = Object.keys(lineItemsByProduct);
    const perProduct = codes.map((code) => selectTargetSubtotal(state, code));
    return volatileSum(...perProduct);
  },
);
